import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Plus,
  Users,
  UserCheck,
  UserX,
  Search,
  ArrowUpRight,
  Pencil,
  MoreHorizontal,
} from 'lucide-react';
import { DataTable, type Column, type TableDensity } from '@/components/common/DataTable';
import { DensityToggle } from '@/components/common/DensityToggle';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { KpiCard } from '@/components/common/KpiCard';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { customersService } from '@/services/customers';
import type { CustomerListRow } from '@/types/customers';
import { useDebounced } from '@/hooks/useDebounced';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { toast } from 'sonner';
import { CustomerFormSheet } from './CustomerFormSheet';
import { CustomerDetailSheet } from './CustomerDetailSheet';
import { MarkAsTestMenuItem } from '@/components/test-data/MarkAsTestMenuItem';
import { TestBadge } from '@/components/test-data/TestBadge';
import { useTestMarks } from '@/components/test-data/useTestMarks';

type StatusFilter = 'all' | 'ACTIVE' | 'INACTIVE';

export function CustomersPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<CustomerListRow | null>(null);
  const [editing, setEditing] = useState<CustomerListRow | null>(null);
  const { isMarked } = useTestMarks();
  const [createOpen, setCreateOpen] = useState(false);
  const [density, setDensity] = useState<TableDensity>('compact');
  const debounced = useDebounced(search);
  const me = useCurrentUser();
  const isTenantAdmin = me?.tier === 'TENANT_ADMIN';

  const handleDelete = (r: CustomerListRow) => {
    if (!window.confirm(`Permanently delete "${r.name}"? This cannot be undone.`)) return;
    customersService
      .remove(r.id)
      .then(() => {
        toast.success(`Deleted ${r.name}`);
        refetch();
      })
      .catch((e: unknown) =>
        toast.error(
          (e as Error)?.message ?? 'Delete failed — archive it instead if it has history.',
        ),
      );
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['customers', { q: debounced }],
    queryFn: () =>
      customersService.list({
        search: debounced || undefined,
        pageSize: 200,
      }),
  });

  const allRows = useMemo(() => data?.items ?? [], [data]);

  const counts = useMemo(() => {
    const c = { all: allRows.length, ACTIVE: 0, INACTIVE: 0 };
    for (const c1 of allRows) c[c1.status] += 1;
    return c;
  }, [allRows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return allRows;
    return allRows.filter((r) => r.status === filter);
  }, [allRows, filter]);

  const summary = useMemo(() => {
    const withGstin = allRows.filter((r) => !!r.gstin).length;
    const totalOrders = allRows.reduce((s, r) => s + r._orderCount, 0);
    return { active: counts.ACTIVE, withGstin, totalOrders };
  }, [allRows, counts.ACTIVE]);

  const tabs: StatusTabOption[] = [
    { value: 'all',      label: 'All',       count: counts.all },
    { value: 'ACTIVE',   label: 'Active',    count: counts.ACTIVE },
    { value: 'INACTIVE', label: 'Archived',  count: counts.INACTIVE },
  ];

  const columns: Column<CustomerListRow>[] = [
    {
      key: 'code',
      header: 'Code',
      width: '120px',
      cell: (r) => <span className="font-mono text-[11.5px] text-muted-foreground">{r.code}</span>,
    },
    {
      key: 'name',
      header: 'Customer',
      cell: (r) => (
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-primary/10 text-[11px] font-semibold text-primary"
          >
            {initials(r.name)}
          </span>
          <div className="min-w-0 space-y-0.5">
            <div className="flex items-center gap-1.5">
              <span className="truncate font-medium text-foreground">{r.name}</span>
              {isMarked('CUSTOMER', r.id) && <TestBadge />}
            </div>
            <div className="truncate text-[11px] text-muted-foreground">
              {r.email ?? r.phone ?? '—'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'gstin',
      header: 'GSTIN',
      width: '170px',
      cell: (r) =>
        r.gstin ? (
          <span className="font-mono text-[11.5px]">{r.gstin}</span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'orders',
      header: 'Orders',
      align: 'right',
      width: '90px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums text-foreground">
          {r._orderCount || '—'}
        </span>
      ),
    },
    {
      key: 'returns',
      header: 'Returns',
      align: 'right',
      width: '90px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums text-muted-foreground">
          {r._returnCount || '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '110px',
      cell: (r) => (
        <Badge
          variant={r.status === 'ACTIVE' ? 'success' : 'muted'}
          className="text-[10px]"
        >
          {r.status === 'ACTIVE' ? 'active' : 'archived'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '100px',
      hoverReveal: true,
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => setSelected(r)}
              >
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Open</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => setEditing(r)}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit customer</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSelected(r)}>Open detail</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setEditing(r)}>Edit customer</DropdownMenuItem>
              <MarkAsTestMenuItem
                entityType="CUSTOMER"
                entityId={r.id}
                marked={isMarked('CUSTOMER', r.id)}
              />
              {isTenantAdmin && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => handleDelete(r)}
                >
                  Delete permanently
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        <ListPageHeader
          title="Customers"
          count={counts.all}
          actions={
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              New customer
            </Button>
          }
        />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <KpiCard label="Active" value={summary.active.toString()} tone="primary" icon={Users} />
          <KpiCard label="With GSTIN" value={summary.withGstin.toString()} tone="default" icon={UserCheck} />
          <KpiCard label="Lifetime orders" value={summary.totalOrders.toString()} tone="default" icon={UserX} />
        </div>

        <StatusTabs value={filter} onChange={(v) => setFilter(v as StatusFilter)} options={tabs} />

        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, code, email, GSTIN…"
              className="h-8 pl-8 text-[13px]"
            />
          </div>
          <DensityToggle value={density} onChange={setDensity} />
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            onRowClick={(r) => setSelected(r)}
            density={density}
            emptyTitle={
              filter === 'all' ? 'No customers yet' : `No ${filter.toLowerCase()} customers`
            }
            emptyDescription={
              filter === 'all'
                ? 'Add your first customer to start booking orders.'
                : 'Try a different status filter.'
            }
            emptyAction={
              filter === 'all' ? (
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  New customer
                </Button>
              ) : null
            }
          />
        )}

        <CustomerDetailSheet
          customer={selected}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
          onEdit={(c) => {
            setSelected(null);
            setEditing(c);
          }}
        />

        <CustomerFormSheet customer={null} open={createOpen} onOpenChange={setCreateOpen} />
        <CustomerFormSheet
          customer={editing}
          open={!!editing}
          onOpenChange={(o) => !o && setEditing(null)}
        />
      </div>
    </TooltipProvider>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
